import {all, call, put, takeLatest, takeLeading} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {getMyPageEpisodes} from './apiGet';
import {callApiMyPageDelete, callApiMyPageTabPayload} from './callApi';
import {pToast} from '../../../common/container/PToast';
import {selectT} from '../../../common/i18n';
import {actions as commonActions} from '../../../common/state/action';
import callApi from '../../../common/util/api';
import {IPagingParam, makeFetchSaga} from '../../../common/util/fetch';
import {typedSelect} from '../../../common/util/saga';
import {routeToMyPage} from '../common';

function* fetchTabSort(action: ReturnType<typeof actions.fetchTabSort>) {
  const {tab, sort} = action.payload;
  const findSort = sort.sortList.find(
    ({sortKey}) => sortKey === sort.currentSort
  );
  const t = yield* selectT();
  if (findSort) {
    // TODO: V4 정렬 API 스펙 나오면 반영하자.
    // TODO: V4 sortInfo 넘길 필요가 있는지 확인하자. (API 에서 변경된 값으로 내려오는지 확인)
    yield put(actions.fetchTabItems({tab, sortInfo: sort}, true));
    yield call(pToast, {
      desc: t(findSort.toastMessage),
      isShowEvery: true,
    });
  }
}

function* fetchTabItems(
  action: ReturnType<typeof actions.fetchTabItems>,
  params: IPagingParam
) {
  const isFirst = action.isFirst;
  const {tab, sortInfo} = action.payload;
  const reduxCurrentSort = yield* typedSelect(
    state => state.myPage.tab[tab]?.currentSort
  );
  const sort = sortInfo?.currentSort || reduxCurrentSort;
  const {offset} = params;
  const {success, recent, like, own} = yield* callApiMyPageTabPayload({
    tab,
    sort,
    offset,
  });
  if (success) {
    yield put(actions.addTabItems({tab, isFirst, recent, own, like}));
    if (like) {
      const likeIds = like.contents.map(item => item.content.id);
      yield put(commonActions.setContentLikes(likeIds, true));
    }
  }
}

function* fetchTabEditItems(
  action: ReturnType<typeof actions.fetchTabEditItems>,
  params: IPagingParam
) {
  const {tab, sort, hideCount = 0} = action.payload;
  const isFirst = action.isFirst;
  const {offset} = params;
  const {success, recent, like, own} = yield* callApiMyPageTabPayload({
    tab,
    offset: offset ? offset - hideCount : undefined,
    isEdit: true,
    sort,
  });
  if (success) {
    yield put(
      actions.addTabItems({tab, isFirst, recent, own, like, isEdit: true})
    );
  }
}

function* fetchDeleteTabItems(
  action: ReturnType<typeof actions.fetchDeleteTabItems>
) {
  const {tab, ids, isRouteToMyPage} = action.payload;
  if (ids.length > 0) {
    yield callApiMyPageDelete(tab, ids);
    if (tab === 'like') {
      yield put(commonActions.setContentLikes(ids, false));
    }
  }

  yield put(actions.fetchTabEditItems({tab}, true));
  yield put(actions.fetchTabItems({tab}, true));

  routeToMyPage(isRouteToMyPage);
}

function* fetchPurchaseContentEpisodes(
  action: ReturnType<typeof actions.fetchPurchaseContentEpisodes>,
  params: IPagingParam
) {
  const {contentId} = action.payload;
  const {success, data} = yield call(callApi, {
    url: '/history/v1/episode-purchased',
    params: {
      contentId,
      offset: params.offset,
      limit: MY_PAGE_LIMIT,
    },
  });

  if (success && data) {
    const t = yield* selectT();
    yield put(
      actions.addMyPageEpisodes(getMyPageEpisodes({data, t}), action.isFirst)
    );
  }
}

function* fetchLikeAlarmToggle(
  action: ReturnType<typeof actions.fetchLikeAlarmToggle>
) {
  const {contentId, isAlarmOn} = action.payload;
  const {success, data} = yield call(callApi, {
    method: 'POST',
    url: '/popularity/v4/subscriptions/update-notification',
    data: {contentId, notificationEnabled: isAlarmOn},
  });

  if (success && data) {
    yield put(actions.setLikeAlarm(contentId, isAlarmOn));
  }
}

function* fetchTogglePurchasedItem(
  action: ReturnType<typeof actions.fetchTogglePurchasedItem>
) {
  const {toggleId, isHidden} = action.payload;
  const {success} = yield call(callApi, {
    method: 'PUT',
    url: `/history/v2/purchased-contents/${toggleId}/${
      isHidden ? 'hide' : 'display'
    }`,
  });
  if (success) {
    yield put(actions.togglePurchasedContent(toggleId, isHidden));
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchTabSort,
      makeFetchSaga({fetchSaga: fetchTabSort, canCache: false})
    ),
    takeLeading(
      ActionType.FetchTabItems,
      makeFetchSaga({fetchSaga: fetchTabItems, canCache: false})
    ),
    takeLatest(
      ActionType.FetchTabEditItems,
      makeFetchSaga({
        fetchSaga: fetchTabEditItems,
        canCache: false,
      })
    ),
    takeLatest(
      ActionType.FetchDeleteTabItems,
      makeFetchSaga({fetchSaga: fetchDeleteTabItems, canCache: false})
    ),
    takeLatest(
      ActionType.FetchPurchaseContentEpisodes,
      makeFetchSaga({
        fetchSaga: fetchPurchaseContentEpisodes,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchLikeAlarmToggle,
      makeFetchSaga({
        fetchSaga: fetchLikeAlarmToggle,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchTogglePurchasedItem,
      makeFetchSaga({fetchSaga: fetchTogglePurchasedItem, canCache: false})
    ),
  ]);
}

export const MY_PAGE_LIMIT = 48;
